import React, { Suspense, useEffect, lazy } from 'react';
import { IonRouterOutlet } from '@ionic/react';
import { Route, Redirect } from 'react-router-dom';
import BottomNav from './BottomNav';
import Sidebar from './Sidebar';
import ErrorBoundary from './ErrorBoundary';
import UndoToast from './UndoToast';
import NotFoundPage from '../pages/NotFound/NotFoundPage';
import { useAuthStore } from '../store/authStore';
import { wsClient } from '../services/websocket';

// Eagerly imported — reached from the home tab on first paint.
import HomePage from '../pages/Home/HomePage';
import ListsHubPage from '../pages/Lists/ListsHubPage';
import TasksPage from '../pages/Tasks/TasksPage';
import ChatPage from '../pages/Chat/ChatPage';
import ProfilePage from '../pages/Profile/ProfilePage';
import PantryPage from '../pages/Pantry/PantryPage';
import MorePage from '../pages/More/MorePage';

// Lazy-loaded — heavier pages that shouldn't block first paint.
const CalendarPage = lazy(() => import('../pages/Calendar/CalendarPage'));
const BudgetPage = lazy(() => import('../pages/Budget/BudgetPage'));
const FamilyLocatorPage = lazy(() => import('../pages/Locator/FamilyLocatorPage'));
const ListDetailPage = lazy(() => import('../pages/Lists/ListDetailPage'));
const RecipesPage = lazy(() => import('../pages/Kitchen/RecipesPage'));
const MealPlannerPage = lazy(() => import('../pages/Kitchen/MealPlannerPage'));
const SchoolCalendarPage = lazy(() => import('../pages/School/SchoolCalendarPage'));
const NHSTrackerPage = lazy(() => import('../pages/Health/NHSTrackerPage'));
const HealthCoachPage = lazy(() => import('../pages/HealthCoach/HealthCoachPage'));
const DocumentsPage = lazy(() => import('../pages/Documents/DocumentsPage'));
const SpecialDaysPage = lazy(() => import('../pages/SpecialDays/SpecialDaysPage'));
const ContactBookPage = lazy(() => import('../pages/Contacts/ContactBookPage'));
const PrivacyPolicyPage = lazy(() => import('../pages/Legal/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('../pages/Legal/TermsOfServicePage'));
const CustomiseHomePage = lazy(() => import('../pages/Settings/CustomiseHomePage'));
const AboutPage = lazy(() => import('../pages/Settings/AboutPage'));
const NotesPage = lazy(() => import('../pages/Notes/NotesPage'));
const RemindersPage = lazy(() => import('../pages/Reminders/RemindersPage'));
const SubscriptionsPage = lazy(() => import('../pages/Subscriptions/SubscriptionsPage'));
const SavingsGoalsPage = lazy(() => import('../pages/Savings/SavingsGoalsPage'));
const DebtTrackerPage = lazy(() => import('../pages/Debt/DebtTrackerPage'));
const FeaturesPage = lazy(() => import('../pages/Features/FeaturesPage'));
const WishlistPage = lazy(() => import('../pages/Wishlist/WishlistPage'));
const PollsPage = lazy(() => import('../pages/Polls/PollsPage'));
const PremiumPage = lazy(() => import('../pages/Premium/PremiumPage'));
const DiscoverPage = lazy(() => import('../pages/Discover/DiscoverPage'));

const AdminRoute: React.FC<{ component: React.ComponentType; path: string; exact?: boolean }> = ({ component: Component, ...rest }) => {
  const { family } = useAuthStore();
  const isAdmin = family?.role === 'admin';
  return (
    <Route {...rest} render={() => isAdmin ? <Component /> : <Redirect to="/app/home" />} />
  );
};

const TabsLayout: React.FC = () => {
  const { refreshFamily } = useAuthStore();

  // Listen for member profile updates (e.g. avatar changes) and refresh family data
  useEffect(() => {
    const unsub = wsClient.on('MEMBER_PROFILE_UPDATED', () => {
      refreshFamily();
    });
    return unsub;
  }, [refreshFamily]);

  // Keyboard shortcuts for desktop
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if ((e.target as HTMLElement)?.isContentEditable) return;

      // Escape to close modals — handled by Ionic natively
      // Ctrl/Cmd + K for search (future)
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="fh-app-layout">
      <UndoToast />
      <Sidebar />
      <a href="#main-content" className="fh-skip-link">Skip to main content</a>
      <ErrorBoundary>
      <main id="main-content" role="main" className="fh-main-content">
      <Suspense fallback={<div className="fh-route-fallback" aria-busy="true" />}>
      <IonRouterOutlet animated={true}>
        {/* Core tabs */}
        <Route exact path="/app/home" component={HomePage} />
        <Route exact path="/app/pantry" component={PantryPage} />
        <Route exact path="/app/kitchen" component={MealPlannerPage} />
        <Route exact path="/app/kitchen/recipes" component={RecipesPage} />
        <Route exact path="/app/lists" component={ListsHubPage} />
        <Route exact path="/app/lists/:listId" component={ListDetailPage} />
        <Route exact path="/app/more" component={MorePage} />
        <Route exact path="/app/features" component={FeaturesPage} />

        {/* Secondary screens */}
        {/* Today folded into Home — redirect kept for deep-links. */}
        <Redirect exact from="/app/today" to="/app/home" />
        <Route exact path="/app/tasks" component={TasksPage} />
        <Route exact path="/app/calendar" component={CalendarPage} />
        <Route exact path="/app/chat" component={ChatPage} />
        <Route exact path="/app/profile" component={ProfilePage} />
        <AdminRoute exact path="/app/budget" component={BudgetPage} />

        {/* New feature pages */}
        <Route exact path="/app/school-calendar" component={SchoolCalendarPage} />
        <Route exact path="/app/health" component={NHSTrackerPage} />
        <Route exact path="/app/health-coach" component={HealthCoachPage} />
        <Route exact path="/app/documents" component={DocumentsPage} />
        <Route exact path="/app/special-days" component={SpecialDaysPage} />
        <Route exact path="/app/discover" component={DiscoverPage} />
        <Route exact path="/app/contacts" component={ContactBookPage} />

        <Route exact path="/app/locator" component={FamilyLocatorPage} />
        <Route exact path="/app/privacy" component={PrivacyPolicyPage} />
        <Route exact path="/app/terms" component={TermsOfServicePage} />
        <Route exact path="/app/customise-home" component={CustomiseHomePage} />
        <Route exact path="/app/about" component={AboutPage} />
        <Route exact path="/app/notes" component={NotesPage} />
        <Route exact path="/app/reminders" component={RemindersPage} />

        {/* Finance sub-pages */}
        <Route exact path="/app/subscriptions" component={SubscriptionsPage} />
        <Route exact path="/app/savings" component={SavingsGoalsPage} />
        <Route exact path="/app/debts" component={DebtTrackerPage} />

        {/* Surfaces surfaced in the Features gallery */}
        {/* Feed consolidated into Chat — keep the redirect so old
            bookmarks/notifications still land somewhere useful. */}
        <Redirect exact from="/app/feed" to="/app/chat" />
        <Route exact path="/app/wishlist" component={WishlistPage} />
        <Route exact path="/app/polls" component={PollsPage} />
        <Route exact path="/app/premium" component={PremiumPage} />

        <Redirect exact from="/app/school-inbox" to="/app/school-calendar" />
        {/* Short-path aliases used by some mobile deep links */}
        <Redirect exact from="/app/docs" to="/app/documents" />
        <Redirect exact from="/app/school" to="/app/school-calendar" />

        <Redirect exact from="/app" to="/app/home" />
        <Route component={NotFoundPage} />
      </IonRouterOutlet>
      </Suspense>
      </main>
      </ErrorBoundary>
      <BottomNav />
    </div>
  );
};

export default TabsLayout;
